import createModule from "../createModule"
import view from "./view"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getBasePositionSetWithSegment, getBaseSignature } from "selectors/base"
import { getSelectedModelId, getShowNotHeld, getIsExpanded, getCurrency, getIsActive } from "modules/portfolio-root/selectors/portfolios"
import { getFetchStartDate } from "selectors/dates"
import { getSelectedGroupValue, getAvailableRollups, getContributorsSummaryText, getCsvData, getGroupBy, getIncludeNotHeld, getSelectedGroupSiblings, getSelectedGroup, getAttributionMethod, getMCTEAdjustedSegment, getSortedContributorsWithoutCashOffset, getGroupedContributorsWithoutCashOffset } from "./selectors"
import { getGroupsDropdownColumns, getDropdownItems } from "./selectors/group"
import { getQueryGroupBy } from "helpers/rollups"
import { classificationsQuery, generateClassificationQueryForContributors, getClassifications } from "helpers/classification"

export default createModule({
  view,
  externalProps: (state, props) => ({
    portfolioId: state.global.selected.portfolio,
    account: state.global.selected.account,
    modelId: getSelectedModelId(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseSignature: getBaseSignature(state, props),
    fetchStartDate: getFetchStartDate(state, props),
    isActive: getIsActive(state, props),
    segment: getMCTEAdjustedSegment(state, props),
    classifications: getClassifications(state, props),
    includeNotHeld: getIncludeNotHeld(state, props),
    expand: getIsExpanded(state, props),
    currency: getCurrency(state, props),
    attributionMethod: getAttributionMethod(state, props),
  }),
  required: ["account", "portfolioId"],
  data: [{
    resource: "contributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, isActive, expand, base, baseSignature, fetchStartDate, segment, classifications, includeNotHeld, currency, attributionMethod }) => {
      if (selectedDate) {
        return {
          id: `riskContributors-${modelId}${portfolioId}${fetchStartDate}${selectedDate}${isActive}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}${attributionMethod}}`,
          query: `query (
              $modelId: String!
              $portfolioId: String!
              $from: Date!
              $to: Date!
              $expand: Boolean
              $base: [PositionSetInput]
              $segment: Segment
              $includeNotHeld: Boolean
              $attributionMethod: RiskContributorAttributionMethod
            ) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $from, to: $to, base: $base, interval: AUTO) {
                riskContributors (on: $to includeNotHeld: $includeNotHeld, attributionMethod: $attributionMethod) {
                  id
                  assetClass
                  assetSubclass
                  percentEquity
                  notHeld
                  total
                  country
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  currency
                  description
                  bloombergTicker
                  modelProviderId
                  attribution {
                    summary {
                      factors
                      specific
                    }
                    factors {
                      id
                      name
                      category
                      value
                    }
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            portfolioId,
            from: fetchStartDate,
            to: selectedDate,
            base: isActive ? [base] : undefined,
            segment,
            includeNotHeld,
            expand,
            attributionMethod,
          },
        }
      } else {
        return null
      }
    },
  }, {
    resource: "classifications",
    as: null,
    url: () => {
      return {
        id: "classifications",
        query: classificationsQuery,
      }
    },
  }, {
    resource: "groupedRiskContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, isActive, expand, base, baseSignature, fetchStartDate, contributorsGrouping, segment, classifications, includeNotHeld, currency, attributionMethod }) => {
      if (!contributorsGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(contributorsGrouping, isActive)

      return {
        id: `groupedRiskContributors-${modelId}${portfolioId}${fetchStartDate}${selectedDate}${JSON.stringify(queryGroupBy)}${isActive}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}${attributionMethod}`,
        query: `query (
            $modelId: String!
            $portfolioId: String!
            $from: Date!
            $to: Date!
            $expand: Boolean
            $base: [PositionSetInput]
            $groupBy: ContributorGroupType!
            $groupById: String
            $tier: String
            $segment: Segment
            $includeNotHeld: Boolean
            $attributionMethod: RiskContributorAttributionMethod
          ) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $from, to: $to, base: $base, interval: AUTO) {
                groupedRiskContributors (on: $to, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier, includeNotHeld: $includeNotHeld, attributionMethod: $attributionMethod) {
                  name
                  id
                  totalPercentEquity
                  attribution {
                    summary {
                      factors
                      specific
                    }
                    factors {
                      id
                      category
                      value
                    }
                  }
                  contributors {
                    id
                    assetClass
                    country
                    notHeld
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    description
                    percentEquity
                    total
                    bloombergTicker
                    modelProviderId
                    attribution {
                      summary {
                        factors
                        specific
                      }
                      factors {
                        id
                        category
                        value
                      }
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          from: fetchStartDate,
          to: selectedDate,
          base: isActive ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          attributionMethod,
          ...queryGroupBy,
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    data: getSortedContributorsWithoutCashOffset(state, props),
    groupedData: getGroupedContributorsWithoutCashOffset(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    csvData: getCsvData(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    showNotHeld: getShowNotHeld(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupBy: getGroupBy(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    groupDropdownItems: getDropdownItems(state, props),
  }),
  actions: {
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
